import { DarkModeOutlined, LightModeOutlined, Menu } from '@mui/icons-material';
import { Popover, Tooltip } from '@mui/material';
import cx from 'classnames';
import { bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import { useEffect, useRef } from 'react';
import { Link } from 'wouter';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import EditButton from '~/elements/Buttons/Edit';
import ViewInDrive from '~/elements/Buttons/ViewInDrive';
import { tooltipSlotProps, tooltipSlots } from '~/elements/Tooltip';
import { darkTheme, lightTheme } from '~/theme.css';
import { isBun, tooltipEnterDelay } from '~/utils/constants';
import { useAuth, useFile, useFiles, useWikis } from '~/utils/hooks/hooks';
import { useSetTheme, useSidebarOpen, useStore, useTheme } from '~/utils/store';
import { getIndexPage, inlineEditingEnabled, isADocument, isAFolder } from '~/utils/utils';
import { AddNewContainer } from '../AddMenu/AddNewContainer';
import { AvatarMenu } from '../AvatarMenu/AvatarMenu';
import { EmptySearch, Search } from '../Search/Search';
import { ShareModal } from '../ShareModal/ShareModal';
import { ShareModalButton } from '../ShareModal/ShareModalContainer';
import * as styles from './Header.css';
import { HeaderMore, type HeaderMoreHandle } from './HeaderMore';

type Props = {
  wikiId: string;
  id?: string | undefined;
};

const throttleMs = 3000;

export const Header = ({ wikiId, id = wikiId }: Props) => {
  const { data: wikis = [] } = useWikis();
  const { data: files } = useFiles(wikiId);
  const { data: wiki } = useFile(wikiId, wikis, wikiId);
  const { data: file } = useFile(id, files, wikiId, true);
  const { anonymous, data: user } = useAuth();
  const theme = useTheme();
  const setTheme = useSetTheme();
  const [sidebarOpen, setSidebarOpen] = useSidebarOpen();
  const indexPage = file ? getIndexPage(file, files) : undefined;
  const page = isAFolder(file) ? indexPage : file;
  const hideViewButton = /false/.test(wiki?.properties?.showViewButtons ?? 'true') && !wiki?.capabilities?.canEdit;
  const newFile = useStore((state) => state.newFile);
  const isNewFolder = newFile && isAFolder(newFile);
  const isEditor = file?.capabilities?.canEdit && !file.trashed;
  const sharePopupState = usePopupState({ variant: 'popover' });
  const headerMoreRef = useRef<HeaderMoreHandle>(null);
  const pageIdRef = useRef<string | undefined>(undefined);
  const hiddenTimeRef = useRef(0);
  const editing = useStore((state) => state.editing);
  const setEditing = useStore((state) => state.setEditing);
  const showInlineEditing = inlineEditingEnabled(wiki, page);

  // Handle visibility cache invalidation
  useEffect(() => {
    if (isADocument(page) && page.capabilities?.canEdit) pageIdRef.current = page?.id;
    else pageIdRef.current = undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!pageIdRef.current) return;
      if (document.visibilityState === 'hidden') {
        hiddenTimeRef.current = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible' || !hiddenTimeRef.current) return;
      const timeAway = Date.now() - hiddenTimeRef.current;
      if (timeAway < throttleMs) return;
      void fetch(`/api/drive/v3/files/${pageIdRef.current}/cache`, { method: 'DELETE', priority: 'high' });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const showUpgrade = Boolean(user && !user.paid && isEditor);

  return (
    <div
      className={cx(styles.container, {
        [styles.editingContainer]: editing,
      })}
    >
      {!editing && (
        <button
          type="button"
          className={styles.tabletBurger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          <Menu style={{ fontSize: 24 }} />
        </button>
      )}
      {!editing &&
        (wiki ? (
          <div style={{ display: 'flex', maxWidth: 400 }}>
            <Search files={files} wiki={wiki} />
          </div>
        ) : (
          <EmptySearch />
        ))}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '.5rem',
          marginLeft: editing ? 'auto' : '.5rem',
          marginRight: showUpgrade && !editing ? -52 : undefined,
        }}
      >
        {!anonymous && page && !newFile && isEditor && (
          <EditButton
            file={page}
            wiki={wiki}
            animation="right"
            keypress
            editing={editing}
            onClick={
              showInlineEditing
                ? (e) => {
                    e.preventDefault();
                    if (!editing) {
                      void fetch(`/api/drive/v3/files/${pageIdRef.current}/cache`, {
                        method: 'DELETE',
                        priority: 'high',
                      });
                    }
                    setEditing(!editing);
                  }
                : undefined
            }
          />
        )}
        {!editing && !anonymous && file && !hideViewButton && !isNewFolder && !file.trashed && (
          <ViewInDrive file={file} email={user?.email} />
        )}
        {!editing && !anonymous && wiki && isEditor && !isNewFolder && (
          <AddNewContainer wiki={wiki} file={file} files={files} animation="right" />
        )}
        {!editing && !showUpgrade && file && wiki && (
          <span className={isEditor ? styles.hideOnTablet : undefined}>
            <ShareModalButton wiki={wiki} wikis={wikis} files={files} user={user} />
          </span>
        )}
        {!editing && !showUpgrade && file && isEditor && (
          <span className={cx(styles.separator, styles.hideOnTablet)}>|</span>
        )}
        {!editing && file && !showUpgrade && (
          <Tooltip
            {...tooltipEnterDelay}
            slotProps={tooltipSlotProps}
            slots={tooltipSlots}
            arrow
            title={theme === 'light' ? 'Switch to dark mode (d)' : 'Switch to light mode (d)'}
          >
            <button
              type="button"
              style={{
                width: 34,
                height: 34,
                marginLeft: isEditor ? '.25rem' : '-.4rem',
                marginRight: isEditor ? 0 : '.35rem',
              }}
              className={cx(styles.buttonCircular, isEditor && styles.hideOnTablet)}
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            >
              {theme === 'light' ? (
                <LightModeOutlined style={{ fontSize: 22 }} />
              ) : (
                <DarkModeOutlined style={{ fontSize: 22 }} />
              )}
            </button>
          </Tooltip>
        )}

        {!editing && !showUpgrade && isEditor && !file.trashed && (
          <Link
            to="/app/settings"
            className={cx(styles.label, styles.hideOnTablet)}
            style={{
              marginLeft: '-.5rem',
            }}
          >
            Settings
          </Link>
        )}
        {!editing && showUpgrade && (
          <Link to="/app/payment" className={buttonStyles.upgrade}>
            Upgrade
            <img src="/img/gift.svg" style={{ width: 14 }} alt="" />
          </Link>
        )}
        {!editing && isEditor && wiki && (
          <>
            <HeaderMore
              ref={headerMoreRef}
              showUpgrade={showUpgrade}
              onShareOpen={() => {
                sharePopupState.setAnchorEl(headerMoreRef.current?.getAnchorEl() ?? null);
                sharePopupState.open();
              }}
            />
            <Popover
              {...bindMenu(sharePopupState)}
              onClose={() => sharePopupState.close()}
              className={cx({
                [darkTheme]: theme === 'dark',
                [lightTheme]: theme === 'light',
              })}
              slotProps={{
                paper: {
                  style: {
                    marginTop: '1rem',
                    marginLeft: 2,
                    color: 'inherit',
                    boxShadow: theme === 'light' ? 'rgba(0, 0, 0, 0.2) 2px 2px 8px 0' : undefined,
                    backgroundColor: 'var(--root-bg-color)',
                    borderRadius: 10,
                  },
                  sx: {
                    overflowX: 'unset',
                    overflowY: 'unset',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -8,
                      right: 26,
                      width: 16,
                      height: 16,
                      backgroundColor: theme === 'light' ? '#fff' : 'inherit',
                      boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 4px 0',
                      transform: 'rotate(-45deg)',
                      clipPath: 'polygon(-5px -5px, calc(100% + 5px) -5px, calc(100% + 5px) calc(100% + 5px))',
                    },
                  },
                },
              }}
              transitionDuration={{
                enter: isBun ? 0 : 200,
                exit: 0,
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <ShareModal
                onCancel={() => sharePopupState.close()}
                file={wiki}
                wiki={wiki}
                wikis={wikis}
                files={files}
                popover
              />
            </Popover>
          </>
        )}
        {!editing && user && <AvatarMenu user={user} />}
        {anonymous && <a href="/app/login">Sign in</a>}
      </div>
    </div>
  );
};
